import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Calendar, Video, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { MediaImage } from '@/components/ui/media-image';
import type { AdminStream } from '@/lib/shared-types/api/endpoints';

interface StreamCardProps {
  stream: AdminStream;
  viewMode?: 'grid' | 'list';
  onRecordingClick?: (streamId: number) => void;
}

export function StreamCard({ stream, viewMode = 'grid', onRecordingClick }: StreamCardProps) {
  const thumbnailUrl = stream.thumbnailUrl || (stream as { thumbnail_url?: string }).thumbnail_url;

  if (viewMode === 'list') {
    return (
      <Card className="@container/admin-stream-list min-h-[104px] hover:shadow-md transition-shadow">
        <CardContent className="p-3 @[760px]/admin-stream-list:p-4">
          <div className="flex h-full flex-col gap-3 @[760px]/admin-stream-list:flex-row @[760px]/admin-stream-list:items-center @[760px]/admin-stream-list:justify-between">
            {/* Thumbnail */}
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-20 h-12 bg-slate-900 rounded overflow-hidden flex-shrink-0">
                {thumbnailUrl ? (
                  <MediaImage src={thumbnailUrl} alt={stream.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-600">
                    <Video className="w-6 h-6" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <Link to={`/admin/streams/${stream.id}`}>
                  <h3 className="font-semibold text-base line-clamp-1 hover:text-primary hover:underline cursor-pointer">
                    {stream.title}
                  </h3>
                </Link>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {stream.scheduledStart ? new Date(stream.scheduledStart).toLocaleDateString() : 'Not scheduled'}
                  </div>
                  {stream.status === 'live' && (
                    <div className="flex items-center gap-1 text-red-600">
                      <Users className="w-3 h-3" />
                      {stream.viewerCount}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Status & Actions */}
            <div className="flex flex-wrap items-center gap-2 flex-shrink-0 @[760px]/admin-stream-list:flex-nowrap">
              <Badge variant={stream.status === 'live' ? 'destructive' : 'secondary'} className="uppercase">
                {stream.status}
              </Badge>
              <Button variant="outline" size="sm" className="w-full @[760px]/admin-stream-list:w-auto" asChild>
                <Link to={`/admin/streams/${stream.id}`}>
                  Manage
                </Link>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Grid view
  return (
    <div className="@container/admin-stream-card group relative bg-card border rounded-lg overflow-hidden hover:shadow-md transition-all">
      <div className="aspect-video bg-slate-900 relative">
        {thumbnailUrl ? (
          <MediaImage src={thumbnailUrl} alt={stream.title} className="w-full h-full object-cover opacity-80" />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-600">
            <Video className="w-12 h-12" />
          </div>
        )}
        <div className="absolute top-2 right-2">
          <Badge variant={stream.status === 'live' ? 'destructive' : 'secondary'} className="uppercase">
            {stream.status}
          </Badge>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <Link to={`/admin/streams/${stream.id}`}>
          <h3 className="font-semibold text-lg line-clamp-1 hover:text-primary hover:underline cursor-pointer">
            {stream.title}
          </h3>
        </Link>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {stream.scheduledStart ? new Date(stream.scheduledStart).toLocaleDateString() : 'Not scheduled'}
          </div>
          {stream.status === 'live' && (
            <div className="flex items-center gap-1 text-red-600">
              <Users className="w-3 h-3" />
              {stream.viewerCount}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2 @[560px]/admin-stream-card:grid-cols-[1fr_auto]">
          <Button variant="outline" className="w-full" asChild>
            <Link to={`/admin/streams/${stream.id}`}>
              Manage Stream
            </Link>
          </Button>
          {onRecordingClick && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRecordingClick(stream.id)}
              title="Manage Recording"
            >
              <Video className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
